import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import './AppLayout.css';

const ICON_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const Icon = {
  LogoMark: (props) => (
    <svg width="14" height="14" {...ICON_PROPS} stroke="#ff5436" strokeWidth="2.5" {...props}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Chevron: (props) => (
    <svg width="14" height="14" {...ICON_PROPS} {...props}>
      <path d="m7 9 5-5 5 5" />
      <path d="m7 15 5 5 5-5" />
    </svg>
  ),
  ChevronRight: (props) => (
    <svg width="14" height="14" {...ICON_PROPS} {...props}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  ),
  Grid: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  ),
  LinkIcon: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Globe: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z" />
    </svg>
  ),
  Bolt: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <path d="M13 2 3 14h9l-1 8 10-12h-9z" />
    </svg>
  ),
  Compass: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  ),
  Folder: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </svg>
  ),
  Tag: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
      <path d="M7 7h.01" />
    </svg>
  ),
  Settings: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Shield: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Search: (props) => (
    <svg width="14" height="14" {...ICON_PROPS} {...props}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  ),
  Bell: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  ),
  Plus: (props) => (
    <svg width="14" height="14" {...ICON_PROPS} strokeWidth="2.5" {...props}>
      <path d="M5 12h14M12 5v14" />
    </svg>
  ),
  Menu: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <path d="M3 12h18M3 6h18M3 18h18" />
    </svg>
  ),
  Close: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  SignOut: (props) => (
    <svg width="14" height="14" {...ICON_PROPS} {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  BarChart: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <path d="M3 3v18h18" />
      <path d="m7 14 4-4 4 4 5-5" />
    </svg>
  ),
  Flask: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <path d="M10 2v7L4.5 19a2 2 0 0 0 1.8 3h11.4a2 2 0 0 0 1.8-3L14 9V2" />
      <path d="M8 2h8" />
    </svg>
  ),
  QrCode: (props) => (
    <svg width="16" height="16" {...ICON_PROPS} {...props}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="2" height="2" />
      <rect x="18" y="14" width="2" height="2" />
      <rect x="14" y="18" width="2" height="2" />
      <rect x="18" y="18" width="2" height="2" />
    </svg>
  ),
};

const SHORT_LINKS = [
  { path: '/dashboard', label: 'Overview',  Icon: Icon.Grid },
  { path: '/links',     label: 'Links',     Icon: Icon.LinkIcon, countKey: 'links' },
  { path: '/domains',   label: 'Domains',   Icon: Icon.Globe },
];

const INSIGHTS = [
  { path: '/analytics',     label: 'Analytics',    Icon: Icon.BarChart },
  { path: '/events',        label: 'Events',        Icon: Icon.Bolt },
  { path: '/ab-tests',      label: 'A/B tests',     Icon: Icon.Flask },
];

const LIBRARY = [
  { path: '/folders', label: 'Folders', Icon: Icon.Folder },
  { path: '/tags',    label: 'Tags',    Icon: Icon.Tag },
  { path: '/qr',      label: 'QR codes', Icon: Icon.QrCode },
];

function isActivePath(pathname, path) {
  if (path === '/dashboard') return pathname === '/dashboard';
  return pathname === path || pathname.startsWith(path + '/');
}

function titleFromPath(pathname) {
  if (pathname.startsWith('/dashboard')) return 'Overview';
  if (pathname.startsWith('/links/')) return 'Link detail';
  if (pathname.startsWith('/links')) return 'Links';
  if (pathname.startsWith('/domains')) return 'Domains';
  if (pathname.startsWith('/analytics')) return 'Analytics';
  if (pathname.startsWith('/events')) return 'Events';
  if (pathname.startsWith('/ab-tests')) return 'A/B tests';
  if (pathname.startsWith('/folders')) return 'Folders';
  if (pathname.startsWith('/tags')) return 'Tags';
  if (pathname.startsWith('/qr')) return 'QR codes';
  if (pathname.startsWith('/utm-templates')) return 'UTM Templates';
  if (pathname.startsWith('/settings')) return 'Settings';
  if (pathname.startsWith('/admin')) return 'Admin';
  return 'Dashboard';
}

function NavItem({ item, counts, active, onNavigate }) {
  const { Icon: IconC, label, path, countKey } = item;
  const count = countKey ? counts[countKey] : null;
  return (
    <Link
      to={path}
      className={`lfa-nav-item ${active ? 'lfa-active' : ''}`}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={onNavigate}
    >
      <IconC className="lfa-nav-icon" />
      <span>{label}</span>
      {count !== null && count !== undefined && count > 0 && (
        <span className="lfa-nav-badge">{count}</span>
      )}
    </Link>
  );
}

function NavSection({ label, items, pathname, counts, onNavigate }) {
  return (
    <div className="lfa-nav-section">
      <div className="lfa-nav-label">{label}</div>
      {items.map((item) => (
        <NavItem
          key={item.path}
          item={item}
          counts={counts}
          active={isActivePath(pathname, item.path)}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user, logout, workspace } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [counts, setCounts] = useState({ links: 0 });

  const handleLogout = useCallback(async () => {
    await logout();
    window.location.href = '/';
  }, [logout]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchValue.trim();
    if (!q) return;
    navigate(`/links?search=${encodeURIComponent(q)}`);
  };

  const closeSidebar = () => setSidebarOpen(false);

  useEffect(() => {
    let alive = true;
    async function fetchCounts() {
      if (!workspace?.workspace_id) return;
      try {
        const { count } = await supabase
          .from('links')
          .select('link_id', { count: 'exact', head: true })
          .eq('workspace_id', workspace.workspace_id);
        if (alive && typeof count === 'number') {
          setCounts((c) => ({ ...c, links: count }));
        }
      } catch {
        /* non-fatal */
      }
    }
    fetchCounts();
    return () => {
      alive = false;
    };
  }, [workspace?.workspace_id, location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        const input = document.querySelector('.lfa-search-input');
        input?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const initials = (user?.name || user?.email || '?').slice(0, 2).toUpperCase();
  const wsInitials = (workspace?.name || 'Workspace').slice(0, 2).toUpperCase();
  const crumb = titleFromPath(location.pathname);
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div className="lfa-root" data-testid="app-layout">
      <div className="lfa-paper" aria-hidden />

      {sidebarOpen && (
        <div
          className="lfa-backdrop"
          onClick={closeSidebar}
          data-testid="sidebar-overlay"
          aria-hidden
        />
      )}

      <aside
        className={`lfa-sidebar ${sidebarOpen ? 'lfa-open' : ''}`}
        data-testid="sidebar"
      >
        <div className="lfa-side-top">
          <Link to="/dashboard" className="lfa-logo" onClick={closeSidebar}>
            <span className="lfa-logo-mark">
              <Icon.LogoMark />
            </span>
            LinkFlow
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="lfa-ws-switch" data-testid="workspace-switcher">
                <span className="lfa-ws-avatar">{wsInitials}</span>
                <span className="lfa-ws-info">
                  <span className="lfa-ws-label">Workspace</span>
                  <span className="lfa-ws-name" data-testid="workspace-name">
                    {workspace?.name || 'My Workspace'}
                  </span>
                </span>
                <Icon.Chevron className="lfa-ws-chevron" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[230px]">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Current
              </DropdownMenuLabel>
              <DropdownMenuItem className="font-medium">
                <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-md bg-[linear-gradient(135deg,#ff5436,#e8b94a)] text-[11px] font-semibold text-white">
                  {wsInitials}
                </span>
                {workspace?.name || 'My Workspace'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                Workspace settings
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <NavSection
          label="Short links"
          items={SHORT_LINKS}
          pathname={location.pathname}
          counts={counts}
          onNavigate={closeSidebar}
        />
        <NavSection
          label="Insights"
          items={INSIGHTS}
          pathname={location.pathname}
          counts={counts}
          onNavigate={closeSidebar}
        />
        <NavSection
          label="Library"
          items={LIBRARY}
          pathname={location.pathname}
          counts={counts}
          onNavigate={closeSidebar}
        />

        <div className="lfa-nav-section">
          <Link
            to="/settings"
            className={`lfa-nav-item ${isActivePath(location.pathname, '/settings') ? 'lfa-active' : ''}`}
            data-testid="nav-settings"
            onClick={closeSidebar}
          >
            <Icon.Settings className="lfa-nav-icon" />
            <span>Settings</span>
          </Link>
          {isSuperAdmin && (
            <Link
              to="/admin"
              className={`lfa-nav-item ${isActivePath(location.pathname, '/admin') ? 'lfa-active' : ''}`}
              onClick={closeSidebar}
            >
              <Icon.Shield className="lfa-nav-icon" />
              <span>Admin</span>
            </Link>
          )}
        </div>

        <div className="lfa-plan-card">
          <div className="lfa-plan-tier">✦ Pro insights</div>
          <div className="lfa-plan-title">
            Unlock <em>AI scoring</em> & retargeting
          </div>
          <p className="lfa-plan-sub">A/B routing, audience pixels and lift forecasting.</p>
          <button
            type="button"
            className="lfa-plan-cta"
            onClick={() => navigate('/settings?tab=billing')}
          >
            Upgrade plan →
          </button>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="lfa-user-row" data-testid="user-menu-trigger">
              <span className="lfa-user-avatar">
                {user?.picture ? <img src={user.picture} alt="" /> : initials}
              </span>
              <span className="lfa-user-info">
                <span className="lfa-user-name">{user?.name || 'User'}</span>
                <span className="lfa-user-email">{user?.email || ''}</span>
              </span>
              <Icon.ChevronRight style={{ color: 'var(--ink-faint)', flexShrink: 0 }} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[220px]">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Account
            </DropdownMenuLabel>
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              Workspace settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </aside>

      <div className="lfa-main">
        <div className="lfa-topbar">
          <button
            type="button"
            className="lfa-mobile-menu"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
            data-testid="mobile-menu-btn"
          >
            <Icon.Menu />
          </button>

          <div className="lfa-breadcrumb">
            <span>{workspace?.name || 'Workspace'}</span>
            <span className="lfa-sep">/</span>
            <span className="lfa-crumb-current">{crumb}</span>
          </div>

          <form onSubmit={handleSearchSubmit} className="lfa-search-bar">
            <span className="lfa-search-icon">
              <Icon.Search />
            </span>
            <input
              type="search"
              className="lfa-search-input"
              placeholder="Search links, tags, domains…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              aria-label="Search"
            />
            <span className="lfa-search-kbd">⌘K</span>
          </form>

          <div className="lfa-topbar-actions">
            <button
              type="button"
              className="lfa-icon-btn"
              aria-label="Notifications"
              onClick={() => navigate('/events')}
            >
              <Icon.Bell />
              <span className="lfa-dot" />
            </button>
            <button
              type="button"
              className="lfa-btn-primary"
              onClick={() => navigate('/links?new=1')}
              data-testid="topbar-create-link-btn"
            >
              <Icon.Plus />
              <span>Create link</span>
            </button>
          </div>
        </div>

        <div className="lfa-content-wrap">{children}</div>
      </div>
    </div>
  );
}
